import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LegalPage from './pages/LegalPage';
import DashboardGate from './pages/DashboardGate';
import MarketplacePage from './pages/MarketplacePage';
import MarketplaceAnalyticsPage from './pages/MarketplaceAnalyticsPage';
import MarketplaceReportsPage from './pages/MarketplaceReportsPage';
import MarketplaceConnectionsPage from './pages/MarketplaceConnectionsPage';
import StocksPage from './pages/StocksPage';
import SyncStatusPage from './pages/SyncStatusPage';
import CostPricePage from './pages/CostPricePage';
import CalculatorPage from './pages/CalculatorPage';
import PromotionsPage from './pages/PromotionsPage';
import TariffsPage from './pages/TariffsPage';
import SettingsPage from './pages/SettingsPage';
import TeamPage from './pages/TeamPage';
import GuidePage from './pages/GuidePage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminBillingPage from './pages/AdminBillingPage';
import AdminWBNewsPage from './pages/AdminWBNewsPage';
import AdminPartnersPage from './pages/AdminPartnersPage';
import PartnerPage from './pages/PartnerPage';
import PartnerJoinPage from './pages/PartnerJoinPage';
import ReferralLinkPage from './pages/ReferralLinkPage';
import Layout from './components/Layout';

// Old addresses (bookmarks, notification links) lead to the same place in the new structure.
const LegacyAnalytics = () => {
  const { marketplace } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/mp/${marketplace}${search}`} replace />;
};
const LegacyRedirect = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/r/:code" element={<ReferralLinkPage />} />
        <Route path="/partner/join" element={<PartnerJoinPage />} />
        <Route path="/legal/offer" element={<LegalPage doc="offer" />} />
        <Route path="/legal/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/legal/consent" element={<LegalPage doc="consent" />} />
        <Route path="/legal/requisites" element={<LegalPage doc="requisites" />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardGate />} />
          <Route path="mp/:marketplace" element={<MarketplacePage />}>
            <Route index element={<MarketplaceAnalyticsPage />} />
            <Route path="reports" element={<MarketplaceReportsPage />} />
            <Route path="costs" element={<CostPricePage />} />
            <Route path="stocks" element={<StocksPage />} />
            <Route path="calculator" element={<CalculatorPage />} />
            <Route path="promotions" element={<PromotionsPage />} />
            <Route path="connections" element={<MarketplaceConnectionsPage />} />
          </Route>
          <Route path="marketplaces/sync/:accountId" element={<SyncStatusPage />} />
          <Route path="tariffs" element={<TariffsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="guide" element={<GuidePage />} />
          <Route path="partner" element={<PartnerPage />} />
          <Route
            path="admin/users"
            element={
              <AdminRoute>
                <AdminUsersPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/billing"
            element={
              <AdminRoute>
                <AdminBillingPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/partners"
            element={
              <AdminRoute>
                <AdminPartnersPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/wb-news"
            element={
              <AdminRoute>
                <AdminWBNewsPage />
              </AdminRoute>
            }
          />
          <Route path="analytics/:marketplace" element={<LegacyAnalytics />} />
          <Route path="marketplaces" element={<Navigate to="/mp/wildberries/connections" replace />} />
          <Route path="marketplace-accounts" element={<Navigate to="/mp/wildberries/connections" replace />} />
          <Route path="cost-prices" element={<LegacyRedirect to="/mp/wildberries/costs" />} />
          <Route path="reports" element={<LegacyRedirect to="/mp/wildberries/reports" />} />
          <Route path="calculator" element={<LegacyRedirect to="/mp/wildberries/calculator" />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

export default App;
